import { intervalToDuration } from "date-fns";
import { NormalInfoWrapper } from "../../tasks/styles";
import {
  OverviewWrapper,
  ProcessHeader,
  ProcessIDWrapper,
  ProcessWrapper
} from "../styles";

// Mirrors apps/flow-setup/setup/process/common/psd2-cleanup/types/state.ts.
type Psd2CleanupState = {
  manualRun?: { batchSize?: number; numberOfBatches?: number };
  criteria?: {
    cutoff: string;
    retentionMonths: number;
    batchSize: number;
    maxNumberOfBatches: number;
    maxNumberOfFlows: number;
    targetFlowId?: string;
  };
  psd2CleanupResult?: {
    numCandidates: number;
    numberOfBatches: number;
    currentBatchNumber: number;
    numAnonymizedFlows: number;
    numSkippedFlows: number;
    numErrors: number;
  };
  failedFlowIds?: string[];
};

const show = (value: unknown, unit = ""): string =>
  value === undefined || value === null || value === ""
    ? "-"
    : `${value}${unit}`;

const showDate = (iso?: string): string =>
  iso ? new Date(iso).toLocaleString() : "-";

const pad = (n?: number): string => String(n ?? 0).padStart(2, "0");

// biome-ignore lint/suspicious/noExplicitAny: the flow type does not know the process state
export const Psd2CleanupOverview = (flow: any) => {
  const caseData = flow?.caseData;
  const state: Psd2CleanupState = caseData?.data ?? {};
  const { criteria, psd2CleanupResult: result, failedFlowIds = [] } = state;

  const createdAt = caseData?.createdAt;
  const completedAt = caseData?.completedAt;
  const duration = createdAt
    ? intervalToDuration({
        start: new Date(createdAt),
        end: new Date(completedAt ?? new Date().toISOString())
      })
    : undefined;

  const rows: [string, string][] = [
    ["Status", show(caseData?.status)],
    ["Started", showDate(createdAt)],
    ["Completed", showDate(completedAt)],
    [
      "Duration",
      duration
        ? `${pad((duration.days ?? 0) * 24 + (duration.hours ?? 0))}:${pad(duration.minutes)}:${pad(duration.seconds)}`
        : "-"
    ],
    ["Manual run", criteria?.targetFlowId || state.manualRun ? "Yes" : "No"],
    ...(criteria?.targetFlowId
      ? [["Single application run", criteria.targetFlowId] as [string, string]]
      : []),
    ["Retention period", show(criteria?.retentionMonths, " months")],
    ["Cut-off date", showDate(criteria?.cutoff)],
    ["Maximum batch size", show(criteria?.batchSize, " flows")],
    ["Maximum number of batches", show(criteria?.maxNumberOfBatches)],
    ["Anonymisation limit", show(criteria?.maxNumberOfFlows, " flows")],
    ["Applications found to anonymise", show(result?.numCandidates)],
    ["Number of batches", show(result?.numberOfBatches)],
    ["Current batch", show(result?.currentBatchNumber)],
    ["Applications anonymised", show(result?.numAnonymizedFlows)],
    ["Applications skipped", show(result?.numSkippedFlows)],
    ["Errors", show(result?.numErrors)]
  ];

  return (
    <OverviewWrapper>
      <ProcessHeader>PSD2 Cleanup Process</ProcessHeader>
      <ProcessWrapper>
        {rows.map(([label, value], index) => (
          <NormalInfoWrapper
            key={label}
            $isLast={failedFlowIds.length === 0 && index === rows.length - 1}
          >
            <div>{label}</div>
            <div>{value}</div>
          </NormalInfoWrapper>
        ))}
        {failedFlowIds.length > 0 ? (
          <>
            <NormalInfoWrapper $header>
              <div>Failed applications (retried by the next run)</div>
            </NormalInfoWrapper>
            {failedFlowIds.map((flowId, index) => (
              <NormalInfoWrapper
                key={flowId}
                $isLast={index === failedFlowIds.length - 1}
              >
                <ProcessIDWrapper>{flowId}</ProcessIDWrapper>
              </NormalInfoWrapper>
            ))}
          </>
        ) : null}
      </ProcessWrapper>
    </OverviewWrapper>
  );
};

export default Psd2CleanupOverview;
